import { getFreshToken } from '@/lib/firebase';

/**
 * Thin authenticated REST client for the staff-api trust/ module
 * (claude-dev trust-center 04-build-spec §4). Same pattern as the GitLab
 * onboarding client: REST + StaffFirebaseGuard, Firebase bearer attached,
 * base = the GraphQL URL with /graphql stripped.
 */
const API_BASE = (import.meta.env.VITE_GRAPHQL_URL as string).replace(/\/graphql\/?$/, '');

// Mirrors TRUST_ARTIFACT_KINDS in apps/api/src/app/trust/trust-artifact.model.ts — keep in sync.
export type TrustArtifactKind =
  | 'soc2'
  | 'soc2-bridge'
  | 'hipaa'
  | 'tx-ramp'
  | 'pentest'
  | 'infosec-policy'
  | 'vpat'
  | 'ndaa-889'
  | 'other';

// Mirrors TRUST_WATERMARKED_KINDS — each link gets its own copy stamped
// "Issued to <email> · <UTC time>", so minting one requires a requester email.
export const WATERMARKED_KINDS: readonly TrustArtifactKind[] = ['soc2-bridge'];

export interface TrustArtifact {
  id: string;
  kind: TrustArtifactKind;
  filename: string;
  version: number;
  sha256: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedAt: string;
  current: boolean;
}

export interface MintLinkResult {
  linkId: string;
  claimUrl: string;
  expiresAt: string;
  artifact: { id: string; kind: string; filename: string; version: number };
}

export interface TrustLogRow {
  id: string;
  createdAt: string;
  createdBy: string;
  via: 'manual' | 'opticlaw';
  requesterName?: string;
  requesterEmail?: string;
  expiresAt: string;
  revokedAt: string | null;
  redeems: Array<{ at: string; ip: string; ua: string }>;
  emailSends?: Array<{ at: string; to: string; by: string }>;
  artifact: { id: string; kind: string; filename: string; version: number } | null;
  request: { id: string; source: string; useCase?: string; createdAt: string } | null;
}

export interface TrustPendingRequest {
  id: string;
  createdAt: string;
  name: string;
  email: string;
  title?: string;
  useCase?: string;
  /** Requested document kind (soc2 | tx-ramp | hipaa | vpat); pre-kind rows arrive as soc2. */
  kind: string;
  source: string;
  turnstileOk: boolean;
}

export interface TrustLog {
  links: TrustLogRow[];
  pendingRequests: TrustPendingRequest[];
}

async function authedFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const { token } = await getFreshToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        // NOTE: no Content-Type here for FormData bodies — the browser sets the
        // multipart boundary itself. JSON callers pass it via init.headers.
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    // See documents-api.ts: fetch also rejects when a response arrived WITHOUT CORS
    // headers, which is what an nginx error page looks like from the browser.
    throw new Error(
      'Could not reach the staff API — the request was rejected before it arrived (network or API gateway).',
    );
  }
  if (!res.ok) {
    let msg = `Request failed (HTTP ${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message) msg = Array.isArray(body.message) ? body.message.join('; ') : body.message;
    } catch {
      // Non-JSON body = the gateway answered, not Nest. Branch on the parse, not the
      // status — Nest's own 413 ('File too large') is a real oversized file.
      if (res.status === 413) {
        msg =
          'The API gateway rejected this upload before it reached the server (HTTP 413 — request too large). The app allows 25 MB; the gateway is capped lower.';
      }
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

const json = { 'Content-Type': 'application/json' };

export const trustApi = {
  listArtifacts: () => authedFetch<{ artifacts: TrustArtifact[] }>('/trust/artifacts'),

  uploadArtifact: (kind: TrustArtifactKind, file: File) => {
    const form = new FormData();
    form.append('kind', kind);
    form.append('file', file);
    return authedFetch<{ artifact: TrustArtifact }>('/trust/artifacts', {
      method: 'POST',
      body: form,
    });
  },

  setCurrent: (artifactId: string) =>
    authedFetch<{ artifact: TrustArtifact }>(`/trust/artifacts/${artifactId}/current`, {
      method: 'POST',
      headers: json,
    }),

  /**
   * `requestId` links the minted link back to the form request it fulfills.
   * Without it the request stays in `pendingRequests` forever — getLog computes
   * pending as "requests with no link pointing at them".
   */
  mintLink: (params: {
    name?: string;
    email?: string;
    kind?: TrustArtifactKind;
    requestId?: string;
  }) =>
    authedFetch<MintLinkResult>('/trust/links', {
      method: 'POST',
      headers: json,
      body: JSON.stringify(params),
    }),

  /**
   * Email the styled claim-link to the requester. The raw token only exists in
   * the claimUrl from mint, so it's passed back for server-side verification.
   */
  sendLinkEmail: (linkId: string, params: { claimUrl: string; email?: string }) =>
    authedFetch<{ ok: boolean; to: string }>(`/trust/links/${linkId}/send`, {
      method: 'POST',
      headers: json,
      body: JSON.stringify(params),
    }),

  revokeLink: (linkId: string) =>
    authedFetch<{ ok: boolean }>(`/trust/links/${linkId}/revoke`, {
      method: 'POST',
      headers: json,
    }),

  log: () => authedFetch<TrustLog>('/trust/log'),
};
